// Pure SVG-geometry maths for KjpChart.vue (INFS-1283). No Vue, no DOM —
// kept plain so it can be exercised by a throwaway Node script the same way
// scripts/verify-kjp.mjs exercises src/kjp.js, rather than guessed at inside
// a component that can only be checked by eye.
//
// The one rule every function here must respect: an "unmeasured" month is
// not a zero. barRects returns null for such a month (no bar drawn) and
// linePoints/lineRuns break the line into separate runs around it (a gap,
// never a bridge and never a point sitting on the zero baseline).

// Maps a value in [0, max] to a pixel in [rangeMin, rangeMax]. max <= 0
// collapses everything to rangeMin instead of dividing by zero — a scope
// with literally no data anywhere (R06 today) must still render axes, not
// throw.
export function linearScale(max, rangeMin, rangeMax) {
  return (v) => (max > 0 ? rangeMin + ((rangeMax - rangeMin) * v) / max : rangeMin)
}

// A y-scale in plot-local pixels: 0 at the bottom (`height`), max at the top
// (0), then shifted down by `top` to become an absolute SVG y. Bars and
// lines share this so a value plots at the same height either way.
export function makeYScale(max, top, height) {
  const s = linearScale(max, height, 0)

  return (v) => top + s(v)
}

// Evenly spaced band centers for n categorical months across [0, width].
export function bandCenters(n, width) {
  if (!n) return []
  const step = width / n

  return Array.from({ length: n }, (_, i) => step * i + step / 2)
}

// Evenly spaced axis tick values from 0 to max (max <= 0 still yields a
// single "0" tick rather than an empty axis).
export function axisTicks(max, count) {
  if (max <= 0) return [0]

  return Array.from({ length: count + 1 }, (_, i) => (max * i) / count)
}

// One rect per month, or null wherever unmeasured[i] is true — never a
// zero-height bar, which would read as "measured, and it was zero".
export function barRects({ values, unmeasured, centers, barWidth, yScale, baselineY }) {
  return values.map((v, i) => {
    if (unmeasured && unmeasured[i]) return null

    const y = yScale(v)

    return {
      x: centers[i] - barWidth / 2,
      y: Math.min(y, baselineY),
      width: barWidth,
      height: Math.abs(baselineY - y),
    }
  })
}

// One point per month, tagged `gap` wherever unmeasured[i] is true. Consumed
// by lineRuns below to break the path rather than plot a false zero.
export function linePoints({ values, unmeasured, centers, yScale }) {
  return values.map((v, i) => ({
    x: centers[i],
    y: yScale(v),
    gap: !!(unmeasured && unmeasured[i]),
  }))
}

// Splits a point list into runs of consecutive non-gap points. A scope
// that's unmeasured in every month (R06's usage line today) yields zero
// runs — the caller draws no path at all, which is the correct picture, not
// a bug to work around.
export function lineRuns(points) {
  const runs = []
  let current = []

  points.forEach((p) => {
    if (p.gap) {
      if (current.length) runs.push(current)
      current = []
    } else {
      current.push(p)
    }
  })
  if (current.length) runs.push(current)

  return runs
}

// SVG path `d` for one run of points — a plain polyline, no smoothing, so
// it reproduces the workbook's chart rather than inventing curvature it
// doesn't have.
export function pathFromPoints(points) {
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')
}

// Which axis group a series def belongs to. Driven entirely by the def's own
// `axis` field (set per-scope in chartSeries.js) — never a conditional on
// scope here — so a scope with no `axis` fields at all (R02, R06) collapses
// every series into the one 'left' group and reproduces the old
// single-shared-axis behaviour exactly.
function axisKeyOf(def) {
  return def.axis || 'left'
}

// Assembles pixel geometry for every series of one scope's chart from
// `projections` (the same object model.projections holds) and the plot's
// pixel box. This is the single place bar/line geometry gets built —
// KjpChart.vue calls it to render, and a throwaway Node script calls the
// exact same function to prove an unmeasured month yields no bar and a line
// gap, rather than the component's own rendering being the only witness.
//
// Dual-axis support (R01): the workbook plots R01's bars against a left
// valAx and its lines against a separate right valAx because the annual
// ceiling line runs ten to twelve times higher than the monthly-allocation
// bar — sharing one axis would squash the bars to a few pixels. Whether a
// scope is dual-axis is entirely a property of its seriesDefs (do any two
// defs disagree on `axis`?), never a scope-name check in this module.
export function buildChartGeometry({ seriesDefs, projections, monthCount, plot }) {
  const { left, top, width, height } = plot
  const centers = bandCenters(monthCount, width).map((x) => x + left)
  const baselineY = top + height

  const barDefs = seriesDefs.filter((s) => s.type === 'bar')
  const barGroupWidth = (width / monthCount) * 0.7
  const barPitch = barDefs.length ? barGroupWidth / barDefs.length : 0
  // 0.8 of the pitch, not the whole pitch, so adjacent bars in a group leave
  // a visible gap instead of touching edge-to-edge.
  const barWidth = barPitch * 0.8

  // One axis per distinct `axis` value present in seriesDefs, each scaled
  // only from the values of series that belong to it. A scope where every
  // def shares (or omits) `axis` ends up with a single 'left' group whose
  // values are the full set — identical to the old shared-max behaviour.
  const axisKeys = [...new Set(seriesDefs.map(axisKeyOf))]
  const buildAxis = (key) => {
    const values = seriesDefs
      .filter((s) => axisKeyOf(s) === key)
      .reduce((acc, s) => acc.concat(projections[s.source] || []), [])
    const max = Math.max(0, ...values)

    return { max, yScale: makeYScale(max, top, height) }
  }
  const axes = axisKeys.reduce((acc, key) => ({ ...acc, [key]: buildAxis(key) }), {})
  if (!axes.left) axes.left = buildAxis('left')

  const series = seriesDefs.map((def) => {
    const axis = axes[axisKeyOf(def)] || axes.left
    const { yScale } = axis
    const values = projections[def.source] || []
    const unmeasured = def.unmeasuredSource ? projections[def.unmeasuredSource] : null

    if (def.type === 'bar') {
      const slot = barDefs.indexOf(def)
      // Correctly centred: the group spans barGroupWidth centred on the
      // month's band center, and barPitch (not barWidth) is the distance
      // between successive bar slots within that span.
      const barCenters = centers.map((c) => c - barGroupWidth / 2 + barPitch * (slot + 0.5))

      return { ...def, bars: barRects({ values, unmeasured, centers: barCenters, barWidth, yScale, baselineY }) }
    }

    const points = linePoints({ values, unmeasured, centers, yScale })

    return { ...def, runs: lineRuns(points).map(pathFromPoints) }
  })

  // `max`/`yScale` alias the left axis for callers that only know about a
  // single shared axis (unchanged for R02/R06); dual-axis consumers read
  // `axes.left`/`axes.right` directly.
  return { series, axes, max: axes.left.max, yScale: axes.left.yScale, centers, baselineY }
}

// Which series of a built geometry are entirely absent from the chart: every
// month unmeasured, so a line series produced zero runs or a bar series has
// every rect null. Shared by KjpChart.vue's on-screen warning banner and
// chartSvg.js's exported markup so both agree on exactly the same set — a
// series flagged absent in one can never be flagged present in the other.
export function absentSeriesKeys(series) {
  return new Set(
    series
      .filter((s) => s.unmeasuredSource)
      .filter((s) => (s.type === 'line' ? s.runs.length === 0 : s.bars.every((b) => b === null)))
      .map((s) => s.key)
  )
}
